<template>
  <div class="page-admin-scraper-health">
    <div class="page-admin-scraper-health__content">
      <!--
        The shared admin header, not a bespoke one. This page used to open with its own <h1>, which
        no other admin page has — the page name is already in the sub-sidebar, so repeating it here
        pushed everything one line down and put this page out of step with every page beside it.
      -->
      <div class="page-admin__table-header">
        <!--
          One control, not two. The fleet at a glance AND the only filter on the page: the old count
          line said "9 à surveiller" without saying how bad — four sources delivering nothing and five
          merely degraded is a very different morning from the reverse — and a separate toggle beside
          it meant two widgets could each narrow the same table.

          The two aggregates come first and are separated from the per-level chips, because "tout" and
          "à surveiller" are answers to a different question than "hors service": how much am I
          looking at, versus what kind of trouble is it.
        -->
        <div class="page-admin-scraper-health__levels">
          <template v-for="(chip, i) in filterChips">
            <span
              v-if="chip.startsGroup && i > 0"
              :key="`${chip.key}-sep`"
              class="page-admin-scraper-health__levels-sep"
            ></span>
            <button
              :key="chip.key"
              type="button"
              class="page-admin-scraper-health__level"
              :class="[
                `page-admin-scraper-health__level--${chip.key}`,
                { 'is-active': filter === chip.key, 'is-aggregate': chip.aggregate },
              ]"
              v-tooltip="chip.help"
              @click="selectFilter(chip.key)"
            >
              <span class="page-admin-scraper-health__level-count">{{ chip.count }}</span>
              {{ chip.label }}
            </button>
          </template>
        </div>

        <div v-if="!isPhone" class="page-admin__actions">
          <!-- refresh(), not the bare handler: `@click.native="refresh"` passes the MouseEvent as the
               options object, and `{ silent = false } = event` only happens to work by accident. -->
          <BaseRefreshButton
            :size="40"
            :pending="pending"
            :tooltip="$t('page.admin-scraper-health.refresh-help')"
            @refresh="refresh()"
          />
        </div>
      </div>

      <!--
        The canary sits above the table, not in it. It is not a source: it queries the app's own
        search path to check that a client can actually find a tender, which makes it the one to
        believe when it disagrees with everything below.

        Two presentations, because it is two different pieces of news. Failing, it is the most
        important thing on the page and keeps the full banner. Passing, it is a reassurance nobody
        needs to read twice, so it collapses into the freshness line — where it sits alongside the
        evaluator's own stamp, since both answer "how much should I trust what is below".
      -->
      <div v-if="canary && canary.state !== 'ok'" class="page-admin-scraper-health__canary" :class="canaryClass">
        <BaseIcon name="alert-triangle" />
        <div>
          <div class="page-admin-scraper-health__canary-title">
            {{ $t('page.admin-scraper-health.canary.title') }}
          </div>
          <div class="page-admin-scraper-health__canary-detail" v-tooltip="absoluteWhen(canary.evaluatedAt)">
            {{ canaryDetail }}
          </div>
        </div>
      </div>

      <!--
        Two clocks sit on this line and they will not agree. The canary is written by
        aloha.api.tender's search task; every other row is written by the indexer's evaluator. Separate
        services, separate five-minute cycles, never in step — so a four-minute spread between them is
        normal, not a fault. The labels have to say which is which, or the line reads as one system
        contradicting itself.

        Keeping them separate is deliberate rather than incidental: the source stamp is how you tell
        the evaluator is alive, and a working canary must never mask a dead evaluator. That is the same
        reason the dead man's switch excludes kind='search'.
      -->
      <p class="page-admin-scraper-health__note">
        <span
          v-if="canary && canary.state === 'ok'"
          class="page-admin-scraper-health__note-ok"
          v-tooltip="canaryTooltip"
        >
          <span class="page-admin-scraper-health__note-dot"></span>
          {{ $t('page.admin-scraper-health.canary.short', { when: formatWhen(canary.evaluatedAt) }) }}
        </span>
        <span
          class="page-admin-scraper-health__note-stamp"
          :class="{ 'page-admin-scraper-health__stamp--stale': isStampStale() }"
          v-tooltip="stampTooltip"
        >
          {{ $t('page.admin-scraper-health.sources-evaluated') }} {{ formatWhen(lastEvaluatedAt) }}
        </span>
        <span>{{ $t('page.admin-scraper-health.legend.timestamps') }}</span>
      </p>

      <div class="page-admin-scraper-health__table-wrapper">
        <TableNormal
          :headers="headers"
          :items="visibleSources"
          :items-per-page="-1"
          :loading="pending"
          :loading-text="$t('page.admin-scraper-health.loading')"
          :no-data-text="$t('page.admin-scraper-health.no-data')"
          :custom-sort="sortKeepingSeverityFirst"
          hide-default-footer
          class="page-admin-scraper-health__table"
          @click:row="onRowClick"
        >
          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count>
            <span class="number-monospace">{{ visibleSources.length }}</span>
            {{ ' ' + $t('page.admin-scraper-health.filter.all') }}
          </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="pending" @refresh="refresh()" />
          </template>
          <template v-slot:progress>
            <BaseLoadingBar v-if="pending" />
          </template>

          <!-- Tooltips on the headers whose meaning is not on the page. Vuetify 2.1.10 has no header
               tooltip option, so each needs its own slot. The tooltip hangs on the "?" rather than on
               the whole heading: firing anywhere over the label gives no hint about what is
               hoverable, and leaves the badge looking decorative. -->
          <template v-for="h in tooltipHeaders" v-slot:[`header.${h}`]="{ header }">
            <span :key="h">
              {{ header.text }}
              <span class="page-admin-scraper-health__help" v-tooltip="$t(`page.admin-scraper-health.tooltips.${h}`)"
                >?</span
              >
            </span>
          </template>

          <template v-slot:item.sourceId="{ item }">
            <!--
              The id is what makes the deep link in an alert work: HealthMessages.link() appends
              "#<sourceId>" to monitor.notify.dashboardUrl, so without an anchor here every alert
              link lands at the top of the table and the operator has to find the row by eye.
            -->
            <span
              :id="item.sourceId"
              class="page-admin-scraper-health__source"
              :class="{ 'page-admin-scraper-health__source--paused': item.paused }"
              >{{ item.sourceId }}</span
            >
            <!--
              Silent unless this row was NOT written by the latest pass. Every lane in a pass is
              stamped from one `now`, so normally all 25 rows carry the identical timestamp and a
              column of it says nothing 25 times over — which is why it is a header stamp now.
              A row that falls behind is the exception that does carry information: it means this
              source stopped being evaluated while the rest of the fleet moved on (dropped from the
              provider list, or its upsert failing), and the fleet stamp would hide exactly that.
            -->
            <span
              v-if="isRowEvaluationBehind(item)"
              class="page-admin-scraper-health__row-stale"
              v-tooltip="
                $t('page.admin-scraper-health.row-stale', {
                  when: formatWhen(item.evaluatedAt),
                  at: absoluteWhen(item.evaluatedAt),
                })
              "
              >⚠</span
            >
          </template>

          <!--
            The answer for someone who does not know what a crawl is. Everything to the right of this
            column explains WHY; this column alone says whether the source is delivering.
          -->
          <template v-slot:item.service="{ item }">
            <BaseTag preset="soft" :tone="serviceTone(item)" v-tooltip="serviceHelp(item)">
              {{ $t(`page.admin-scraper-health.service.${serviceLevel(item)}`) }}
            </BaseTag>
          </template>

          <template v-slot:item.crawl="{ item }">
            <ScraperHealthLaneCell :lane="item.crawl" />
          </template>

          <template v-slot:item.scrape="{ item }">
            <ScraperHealthLaneCell :lane="item.scrape" />
          </template>

          <template v-slot:item.index="{ item }">
            <ScraperHealthLaneCell :lane="item.index" />
          </template>

          <!--
            Only the backlog that actually drives state is highlighted. A source can carry a huge
            historical backlog and still be correctly `ok`: nothing drains failed_jobs automatically,
            so rows older than failedBacklogHorizon are deliberately excluded from the verdict.
            hydroquebec has 151,910 open failures and is healthy. Painting that red taught the reader
            the page cries wolf, which is exactly what the horizon exists to prevent.
          -->
          <template v-slot:item.failedOpenCount="{ item }">
            <span v-if="item.failedOpenCount === null || item.failedOpenCount === undefined">—</span>
            <span
              v-else
              :class="{
                'page-admin-scraper-health__bad-number': backlogDrivesState(item) && !item.paused,
                'page-admin-scraper-health__muted': item.paused,
              }"
              v-tooltip="backlogTitle(item)"
            >
              {{ item.failedOpenCount }}
            </span>
          </template>

          <template v-slot:item.solrNewestAt="{ item }">
            <span
              :class="{ 'page-admin-scraper-health__muted': item.paused }"
              v-tooltip="absoluteWhen(item.index && item.index.solrNewestAt)"
            >
              {{ formatWhen(item.index && item.index.solrNewestAt) }}
            </span>
          </template>
        </TableNormal>
      </div>
    </div>

    <ScraperHealthDetailDialog ref="detailDialog" @action-succeeded="refresh" />
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import ScraperHealthDetailDialog from '@/components/page-admin-scraper-health/ScraperHealthDetailDialog';
import ScraperHealthLaneCell from '@/components/page-admin-scraper-health/ScraperHealthLaneCell';
import TableNormal from '@/components/table/TableNormal.vue';
import { scraperHealthService } from '@/services/scraper-health.service';
import { getScraperHealthServiceTone } from '@/util/tag-tones/scraper-health-state-tag';
import { viewport } from '@/util/viewport';
import {
  EVALUATION_STALE_MS,
  formatAbsolute,
  formatRelativeToNow,
  getSourceServiceLevel,
} from '@/util/scraper-health-util';

/**
 * Sort weight per lane state — deliberately INVERTED, worst = lowest.
 *
 * Vuetify's first click on a column sorts ascending. With a natural severity scale that means the
 * first click on "crawl" answers "which sources are paused", which is the least useful question on
 * the page. Inverted, one click answers "which crawls are worst", which is why someone clicked.
 *
 * Numbers and dates keep their conventional direction: ascending on a date already means oldest —
 * stalest — first, and inverting a count would just be surprising.
 */
/**
 * How often the page re-reads the overview while it is open.
 *
 * Not matched to the evaluator's own 5-minute cadence, deliberately. Polling cannot produce data
 * sooner than the evaluator writes it, so the interval is not about freshness of the verdicts — it is
 * about the freshness of the *freshness stamp*. At one minute, an evaluator that dies shows up in the
 * header within a minute or two; at five, a viewer could sit in front of a stale page for ten.
 *
 * One request a minute against a 74-row read, for a page a couple of admins have open at a time.
 */
const AUTO_REFRESH_MS = 60 * 1000;

const LANE_SORT_SEVERITY = { failing: 1, stale: 2, unknown: 3, ok: 4 };
const LANE_SORT_PAUSED = 5;
const LANE_SORT_ABSENT = 6;

/** Same inverted-scale reasoning as LANE_SORT_SEVERITY: one click sorts worst-first. */
const SERVICE_SORT = { down: 1, degraded: 2, unknown: 3, ok: 4, paused: 5 };

export default {
  name: 'PageAdminScraperHealth',
  components: {
    TableNormal,
    BaseIcon,
    BaseLoadingBar,
    BaseRefreshButton,
    BaseTag,
    ScraperHealthDetailDialog,
    ScraperHealthLaneCell,
  },
  data() {
    return {
      sources: [],
      canary: null,
      problemCount: 0,
      pausedCount: 0,
      pending: false,
      /**
       * The one filter on the page: 'all', 'problems', or a service level. Defaults to 'all' — a
       * filter that hides rows by default is how a source stays quietly broken, because the reader
       * has no way to know the list is incomplete.
       */
      filter: 'all',
      autoRefreshTimer: null,
      /**
       * Bumped on every tick purely to invalidate the relative timestamps.
       *
       * Every "il y a 4 min" on this page is computed against `Date.now()` at render time, so without
       * something reactive changing, they freeze at whatever they said when the data last loaded — and
       * the "Dernière évaluation" stamp would keep claiming 4 minutes indefinitely, including in the
       * one case where it matters most: fetches failing while the page sits open. Referencing this in
       * the stamp re-renders the component each tick, so every elapsed time on the page ages honestly
       * whether or not new data arrived.
       */
      nowTs: Date.now(),
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    /**
     * Every column sorts. The three lane columns hold objects, so Vuetify's default comparator could
     * not order them and they were marked unsortable — but "show me the worst crawl lanes" is the
     * question this page exists to answer, so they sort by state severity via `sortKeepingSeverityFirst`.
     */
    headers() {
      return [
        // Widths trimmed when the Service column was added: eight columns have to fit beside the
        // settings sub-sidebar without the reader scrolling sideways to see whether Solr is current.
        { text: this.$t('page.admin-scraper-health.columns.source'), value: 'sourceId' },
        { text: this.$t('page.admin-scraper-health.columns.service'), value: 'service', phone: 'meta', width: '112px' },
        { text: this.$t('page.admin-scraper-health.columns.crawl'), value: 'crawl', width: '116px' },
        { text: this.$t('page.admin-scraper-health.columns.scrape'), value: 'scrape', width: '116px' },
        { text: this.$t('page.admin-scraper-health.columns.index'), value: 'index', width: '116px' },
        {
          // "Échecs récents" / "Recent failures", not a bare "Échecs" / "Failures". Bounded twice over
          // and a bare "failures" heading claims neither bound: it counts only failures still
          // UNRESOLVED, and only those inside failedBacklogHorizon (7d) — the same window the state is
          // decided from. It used to be all-time, which is what the older wording was hedging around;
          // now the count and the date beside it describe one population, so the heading can say so
          // outright. A heading that needs its own tooltip to be read correctly is a heading that is
          // wrong.
          text: this.$t('page.admin-scraper-health.columns.failed-backlog'),
          value: 'failedOpenCount',
          width: '120px',
        },
        // Wide enough for the worst case, "il y a 182 j 7 h". It wrapped at 104px — on exactly the
        // two most alarming rows, which is where a broken line reads as a broken page.
        {
          text: this.$t('page.admin-scraper-health.columns.solr-freshness'),
          value: 'solrNewestAt',
          width: '116px',
        },
        // No `evaluatedAt` column. Every row is written by one evaluator pass, so it held a single
        // identical value repeated 25 times across 104px. It is a page-level fact and now lives in
        // the header stamp.
      ];
    },
    /** Headers that get a "?" tooltip. Keyed by the header `value`, which the slot name needs. */
    tooltipHeaders() {
      return ['crawl', 'scrape', 'index', 'failedOpenCount', 'solrNewestAt'];
    },
    visibleSources() {
      return this.orderByService(this.sources.filter((s) => this.matchesFilter(s)));
    },
    /**
     * The chip row: two aggregates, then one chip per service level actually present.
     *
     * A level nobody is in gets no chip — an empty "0 Hors service" reads as a control rather than as
     * good news. The aggregates are always shown, "0 à surveiller" included, because there the zero
     * IS the news.
     */
    filterChips() {
      const counts = { down: 0, degraded: 0, unknown: 0, ok: 0, paused: 0 };
      this.sources.forEach((s) => {
        const level = getSourceServiceLevel(s);
        if (counts[level] !== undefined) counts[level] += 1;
      });

      const aggregates = [
        {
          key: 'all',
          count: this.sources.length,
          label: this.$t('page.admin-scraper-health.filter.all'),
          help: this.$t('page.admin-scraper-health.filter.all-help'),
          aggregate: true,
          startsGroup: false,
        },
        {
          key: 'problems',
          count: this.problemCount,
          label: this.$t('page.admin-scraper-health.filter.problems'),
          help: this.$t('page.admin-scraper-health.filter.problems-help'),
          aggregate: true,
          startsGroup: false,
        },
      ];

      const levels = ['down', 'degraded', 'unknown', 'ok', 'paused']
        .filter((key) => counts[key] > 0)
        .map((key, i) => ({
          key,
          count: counts[key],
          label: this.$t(`page.admin-scraper-health.service.${key}`),
          help: this.$t(`page.admin-scraper-health.service-help.${key}`),
          aggregate: false,
          startsGroup: i === 0,
        }));

      return [...aggregates, ...levels];
    },
    /**
     * The newest evaluation across the fleet. Max rather than min: a single source that failed to be
     * measured must not make the whole monitor look stale — that source shows `unknown` on its own row,
     * which is the accurate place to say so.
     */
    lastEvaluatedAt() {
      return this.sources.reduce((newest, s) => {
        if (!s.evaluatedAt) return newest;
        return !newest || new Date(s.evaluatedAt) > new Date(newest) ? s.evaluatedAt : newest;
      }, null);
    },
    /**
     * Each tooltip gives the absolute date and says whose clock it is — nothing more. The canary names
     * its writer because that alone is what explains why the two times differ; spelling the comparison
     * out turns a label into an essay.
     */
    stampTooltip() {
      return this.$t('page.admin-scraper-health.sources-evaluated-help', {
        at: this.absoluteWhen(this.lastEvaluatedAt),
      });
    },
    canaryTooltip() {
      const at = this.canary ? this.absoluteWhen(this.canary.evaluatedAt) : '';
      return this.$t('page.admin-scraper-health.canary.schedule-help', { at });
    },
    canaryClass() {
      return this.canary && this.canary.state === 'ok'
        ? 'page-admin-scraper-health__canary--ok'
        : 'page-admin-scraper-health__canary--bad';
    },
    canaryDetail() {
      if (!this.canary) return '';
      return this.$t(
        this.canary.state === 'ok' ? 'page.admin-scraper-health.canary.ok' : 'page.admin-scraper-health.canary.failing',
        {
          when: formatRelativeToNow(this.canary.evaluatedAt, this.$i18n.locale),
          // Translated, not raw. This printed the bare reason code — "(search_returned_nothing)" —
          // in the one banner on the page that only ever appears when something is badly wrong, and
          // is therefore the least useful moment to start speaking in identifiers.
          reason: this.reasonText(this.canary.reason),
        },
      );
    },
  },
  methods: {
    /**
     * @param silent true for the background poll: no loading bar, and no toast on failure.
     *
     * A full-width loading bar flashing every minute would make a working page look like a struggling
     * one, and a toast every minute during an outage is worse than the outage. A silent failure is not
     * hidden, though — `sources` is left untouched, so the header stamp keeps ageing and turns red on
     * its own once the data it describes is 20 minutes old.
     */
    refresh({ silent = false } = {}) {
      if (!silent) this.pending = true;

      return scraperHealthService
        .getOverview()
        .then((overview) => {
          this.sources = overview.sources;
          this.canary = overview.canary;
          this.problemCount = overview.problemCount;
          this.pausedCount = overview.pausedCount;
        })
        .catch(() => {
          if (!silent) this.$toast.error(this.$t('page.admin-scraper-health.load-error'));
        })
        .finally(() => {
          if (!silent) this.pending = false;
        });
    },
    startAutoRefresh() {
      this.autoRefreshTimer = window.setInterval(this.onTick, AUTO_REFRESH_MS);
      // Refresh the moment the tab is looked at again, rather than waiting out the interval: that is
      // both when the data is stalest and when someone is about to make a decision on it.
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    },
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        window.clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible') this.onTick();
    },
    onTick() {
      // The clock advances even when the fetch is skipped — that is the whole point of it being
      // separate. A backgrounded tab still ages its timestamps, so returning to it shows an honest
      // "il y a 47 min" rather than the "il y a 2 min" it froze at.
      this.nowTs = Date.now();

      if (document.visibilityState !== 'visible') return;
      if (this.pending) return;
      // Never pull the table out from under an open dialog: the dialog holds its own copy of the row,
      // so a reload behind it would leave the two showing different numbers for the same source.
      if (this.isDetailDialogOpen()) return;

      this.refresh({ silent: true });
    },
    isDetailDialogOpen() {
      const dialog = this.$refs.detailDialog;
      return Boolean(dialog && dialog.$refs && dialog.$refs.dialog && dialog.$refs.dialog.isModalOpen);
    },
    onRowClick(source) {
      this.$refs.detailDialog.open(source);
    },
    /**
     * Bring the source named in the URL fragment into view, and mark it briefly.
     *
     * The highlight is not decoration: arriving from an email, the reader has no way to tell which of
     * 25 rows the link meant once the page has scrolled, and a row that is merely centred is not
     * obviously the answer to anything.
     */
    scrollToLinkedSource() {
      const id = decodeURIComponent(String(window.location.hash || '').replace(/^#/, ''));
      if (!id) return;

      this.$nextTick(() => {
        const el = document.getElementById(id);
        if (!el) return;

        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        const row = el.closest('tr');
        if (!row) return;

        row.classList.add('page-admin-scraper-health__row--linked');
        window.setTimeout(() => row.classList.remove('page-admin-scraper-health__row--linked'), 2400);
      });
    },
    formatWhen(value) {
      return value ? formatRelativeToNow(value, this.$i18n.locale) : '—';
    },
    /**
     * The absolute date behind a relative one. Every "x ago" on this page carries it as a title,
     * because the one thing you cannot recover from "181 d 23 h ago" is the actual date — and on an
     * operations page the date is what you paste into a ticket or match against a deploy.
     */
    absoluteWhen(value) {
      return value ? formatAbsolute(value) : '';
    },
    /**
     * The default row order: by the Service column, worst first.
     *
     * The API sorts by `overallState`, its max-severity rollup, and that used to be the only ranking
     * on the page. It is not any more, and the two disagree — infrastructure_ontario is merely
     * `stale` by max severity but `down` by service (its crawl finds nothing, so clients get nothing),
     * while bidsandtenders is `failing` by max severity but only `degraded` (tenders still arrive).
     * Leaving the API order in place meant the leftmost column of meaning contradicted the row order:
     * "Hors service" rows appeared below "Partielle" ones.
     *
     * Stable sort, so within one service level the API's own ordering — severity, then source id —
     * still decides, and this only regroups rather than reshuffling.
     */
    orderByService(list) {
      return [...list].sort(
        (a, b) => (SERVICE_SORT[getSourceServiceLevel(a)] || 99) - (SERVICE_SORT[getSourceServiceLevel(b)] || 99),
      );
    },
    /**
     * Reason codes come from the evaluator. Fall back to the raw code rather than a missing-key
     * placeholder: an unexplained but visible code is still actionable, and a check added on the
     * back end must not blank the message out entirely.
     */
    reasonText(reason) {
      if (!reason) return '';
      const key = `page.admin-scraper-health.reasons.${reason}`;
      return this.$te(key) ? this.$t(key) : reason;
    },
    /** Clicking the active chip returns to 'all'; 'all' itself is the resting state, not a toggle. */
    selectFilter(key) {
      this.filter = this.filter === key ? 'all' : key;
    },
    /**
     * Paused sources are excluded from "à surveiller" as well as problem-free ones: someone already
     * owns them, so they are not what that filter is for. Selecting the "En pause" chip is how you
     * look at them deliberately.
     */
    matchesFilter(source) {
      if (this.filter === 'all') return true;
      if (this.filter === 'problems') {
        return !source.paused && getSourceServiceLevel(source) !== 'ok';
      }
      return getSourceServiceLevel(source) === this.filter;
    },
    serviceLevel(source) {
      return getSourceServiceLevel(source);
    },
    serviceTone(source) {
      return getScraperHealthServiceTone(getSourceServiceLevel(source));
    },
    /** Says which lane is responsible, so the rollup is a starting point rather than a dead end. */
    serviceHelp(source) {
      return this.$t(`page.admin-scraper-health.service-help.${getSourceServiceLevel(source)}`);
    },
    /**
     * Whether the backlog is what put a lane in its current state.
     *
     * Keyed on the reason, not on `failedOldestAt` merely existing. That used to be the right test
     * because the count was all-time and the date was horizon-bounded, so a date meant "some of these
     * are recent". Now the count is horizon-bounded too, the two always agree, and the old test turned
     * red for any non-zero number at all — including a handful of failures from an hour ago, which
     * trip no threshold and mean nothing yet.
     */
    backlogDrivesState(source) {
      return (source.lanes || []).some((lane) => lane.reason === 'failed_jobs_age');
    },
    /**
     * Which lanes the number is made of. The column sums crawl and scrape, and each source is usually
     * dominated by one of them — 167 crawl breakages on newfoundland_and_labrador, 4 missing tender
     * details on biddingo — so the total alone cannot say which kind of problem it is.
     */
    backlogTitle(source) {
      if (!source.failedOpenCount) return '';

      return (source.lanes || [])
        .filter((lane) => lane.failedOpenCount)
        .map((lane) =>
          this.$t('page.admin-scraper-health.backlog.by-lane', {
            count: lane.failedOpenCount,
            lane: this.$t(`page.admin-scraper-health.columns.${lane.kind}`).toLowerCase(),
          }),
        )
        .join(', ');
    },
    /**
     * Whether this row missed the latest evaluation pass.
     *
     * Compared against the newest stamp in the fleet rather than against the clock, because that is
     * the only comparison that isolates ONE source falling behind from the whole monitor being late
     * — the second case is already reported once, in the header, and does not need repeating on
     * every row.
     */
    isRowEvaluationBehind(source) {
      if (!this.lastEvaluatedAt) return false;
      if (!source.evaluatedAt) return true;
      return new Date(source.evaluatedAt).getTime() < new Date(this.lastEvaluatedAt).getTime();
    },
    /**
     * Reads `nowTs` rather than `Date.now()` so the template re-evaluates it on every tick — see the
     * field's own note. Without that reactive dependency the stamp freezes at whatever it said when the
     * data last loaded, which is precisely wrong when loading is what has stopped working.
     */
    isStampStale() {
      return this.nowTs > 0 && this.isEvaluationStale(this.lastEvaluatedAt);
    },
    isEvaluationStale(evaluatedAt) {
      // No evaluation at all counts as stale: on this page, "not measured" must never look calmer
      // than "measured and bad".
      if (!evaluatedAt) return true;
      return Date.now() - new Date(evaluatedAt).getTime() > EVALUATION_STALE_MS;
    },
    /**
     * With no column chosen, keep the order `visibleSources` already established — worst service
     * level first. Vuetify's default sort would replace it with an alphabetical sort on the first
     * column and bury every broken source.
     */
    sortKeepingSeverityFirst(items, sortBy, sortDesc) {
      if (!sortBy || !sortBy.length) return items;

      const key = sortBy[0];
      const desc = Boolean(sortDesc && sortDesc[0]);

      return [...items].sort((a, b) => {
        const av = this.sortValue(a, key);
        const bv = this.sortValue(b, key);
        if (av === bv) return 0;
        // Missing values sort last either way: a source with nothing measured is not "the smallest".
        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;
        const result = av < bv ? -1 : 1;
        return desc ? -result : result;
      });
    },
    /**
     * A comparable value per column.
     *
     * The lane columns hold objects, so they sort on state severity — see LANE_SORT_SEVERITY for why
     * that scale is inverted. Paused lanes and absent lanes sort last in both directions' worth of
     * usefulness: neither is a problem anybody can act on.
     */
    sortValue(item, key) {
      if (key === 'service') {
        return SERVICE_SORT[getSourceServiceLevel(item)] || LANE_SORT_ABSENT;
      }
      if (key === 'crawl' || key === 'scrape' || key === 'index') {
        const lane = item[key];
        if (!lane) return LANE_SORT_ABSENT; // listing-only source, no such lane
        if (item.paused) return LANE_SORT_PAUSED;
        return LANE_SORT_SEVERITY[lane.state] || LANE_SORT_ABSENT;
      }
      if (key === 'solrNewestAt') {
        const at = item.index && item.index.solrNewestAt;
        return at ? new Date(at).getTime() : null;
      }
      return item[key];
    },
  },
  created() {
    // Scroll only after the rows exist. Every alert links to `…/scraper-health#<sourceId>`, and the
    // browser resolves that fragment on load — before this component has fetched anything, so the
    // anchor it is looking for is not in the DOM yet and nothing happens. Every alert link therefore
    // landed at the top of a 25-row table, leaving the operator to find the source by eye, which is
    // exactly what the per-row anchors exist to prevent.
    this.refresh().then(() => this.scrollToLinkedSource());
    this.startAutoRefresh();
  },
  // Without this the interval outlives the route: requests keep firing against a destroyed component,
  // and the visibilitychange listener keeps a reference to it alive for the rest of the session.
  beforeDestroy() {
    this.stopAutoRefresh();
  },
};
</script>

<style lang="scss">
.page-admin-scraper-health {
  // 32px matches every other admin page. It was 20px to buy horizontal room for seven columns, but
  // being the one page inset differently from its neighbours was the more visible cost; the table
  // wrapper scrolls if it needs to.
  &__content {
    padding: 32px;
  }

  &__levels {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  // Separates "how much am I looking at" from "what kind of trouble is it". A gap alone did not
  // read as a boundary once the row wrapped.
  &__levels-sep {
    width: 1px;
    height: 20px;
    background: $color-slate-200;
    margin: 0 2px;
  }

  &__level {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px 5px 9px;
    // A visible resting border, not a transparent one. On slate-100 against a white card these read
    // as plain text until hovered, so nothing suggested they filter anything.
    border: 1px solid $color-slate-200;
    border-radius: $border-radius-root;
    background: $color-slate-100;
    color: $color-slate-700;
    font-size: 13px;
    cursor: pointer;
    outline: none;
    transition: border-color 0.12s ease, background 0.12s ease;

    &:hover:not(:disabled) {
      border-color: $color-slate-400;
    }

    &.is-active {
      border-color: $color-slate-600;
      background: $color-white;
      font-weight: 600;
    }

    &:focus-visible {
      box-shadow: 0 0 0 2px $color-accent;
    }
  }

  // The count carries the level's colour and the label stays neutral. Colouring whole chips put five
  // saturated blocks above a table that also uses colour to mean something — they competed.
  &__level-count {
    font-weight: 700;
    font-size: 15px;
    font-variant-numeric: tabular-nums;
  }

  &__level--down &__level-count {
    color: $color-semantic-error;
  }

  &__level--degraded &__level-count {
    color: $color-semantic-warning;
  }

  &__level--unknown &__level-count {
    color: $color-orange;
  }

  &__level--ok &__level-count {
    color: $color-semantic-success;
  }

  &__level--paused &__level-count {
    color: $color-slate-500;
  }

  // The aggregates carry no colour: they are a quantity, not a verdict. Colouring them would imply
  // "25 sources" is itself good or bad news.
  &__level.is-aggregate &__level-count {
    color: $color-slate-800;
  }

  &__stamp {
    // .page-admin__actions is shared with other admin pages and does not centre its children, so the
    // stamp sat on the toggle's top edge rather than its midline. Scoped here rather than fixed there.
    align-self: center;
    font-size: 12px;
    color: $color-text-muted;
    margin-right: 12px;
    white-space: nowrap;

    &--stale {
      color: $color-semantic-error;
      font-weight: 600;
    }
  }

  // One quiet caption line, deliberately not a bordered block: it belongs to the table below it, so
  // it should read as a caption on that table rather than as a panel of its own.
  // One caption line carrying everything that qualifies the table: whether a client can actually
  // search, how fresh the numbers are, and how to read the timestamps. Three separate blocks for
  // three sentences was most of the "bolted on" feeling.
  &__note {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin: 0 0 12px;
    font-size: 12px;
    color: $color-text-muted;
  }

  &__note-ok,
  &__note-stamp {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    cursor: help;
  }

  &__note-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: $color-semantic-success;
    flex: none;
  }

  // Contrast, not decoration. This was $color-text-muted (#b1bbc7) on $color-grey-pale (#dddddd):
  // a ratio of 1.43:1 on a 10px glyph, which is why these read as "not rendering" — they were in
  // the DOM the whole time and simply could not be seen. slate-800 on slate-200 is 7.07:1.
  // Centring via flex, not line-height. line-height only centres if it exactly matches the box, and
  // it never quite does across fonts — the glyph sat a pixel high and looked off in its circle.
  &__help {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: $color-slate-200;
    color: $color-slate-800;
    font-size: 10px;
    font-weight: 700;
    // The "?" glyph carries its own left side bearing, so it optically sits right of centre in a
    // circle that is mathematically centred. A sub-pixel nudge is what makes it look centred.
    text-indent: 0.5px;
    cursor: help;
    vertical-align: middle;
    margin-left: 5px;
    transition: background 0.12s ease;

    &:hover {
      background: $color-slate-300;
    }
  }

  &__muted {
    color: $color-text-muted;
  }

  // Silent unless this row missed the latest pass — see isRowEvaluationBehind.
  // Fades out on its own: it answers "which row did the email mean" and then stops being useful.
  &__table tbody tr.page-admin-scraper-health__row--linked {
    background: $color-semantic-info-lighter;
    box-shadow: inset 3px 0 0 0 $color-accent;
    transition: background 400ms ease;
  }

  &__row-stale {
    margin-left: 6px;
    color: $color-semantic-error;
    font-size: 13px;
    cursor: help;
  }

  // Paused sources recede. Applied per cell rather than to the row: Vuetify 2.1.10 has no
  // `item-class` prop (added in 2.3.0), so the row-level version was silently ignored and did
  // nothing at all. The leftmost column carries the signal, so it still reads as row-level.
  &__source--paused {
    color: $color-text-muted;
    font-style: italic;
  }

  &__muted {
    color: $color-text-muted;
  }

  &__table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  &__source {
    font-family: monospace;
    font-size: 13px;
  }

  &__canary {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    margin: 0 16px 16px;
    border-radius: 8px;

    svg {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    &--ok {
      background: $color-semantic-success-lighter;
      color: $color-semantic-success;
    }

    &--bad {
      background: $color-semantic-error-lighter;
      color: $color-semantic-error;
    }
  }

  &__canary-title {
    font-weight: 600;
  }

  &__canary-detail {
    font-size: 13px;
  }

  &__bad-number {
    color: $color-semantic-error;
    font-weight: 600;
  }

  &__table tbody tr {
    cursor: pointer;
  }

  // Elapsed times never wrap: "il y a 182 j 7 h" split across two lines reads as two values.
  &__table tbody td:nth-last-child(-n + 2) {
    white-space: nowrap;
  }

  // Nor do headings. A wrapped heading strands its "?" badge on the first line, where it reads as a
  // badge belonging to half a word. Columns are sized to fit their label instead.
  //
  // The long selector is not defensive habit, it is the minimum that wins. Vuetify ships
  // `.v-data-table.theme--light .v-data-table-header tr th span { white-space: normal }` at
  // specificity (0,3,3); anything shorter is inherited from the `th` and then overridden on the
  // span, and the label wraps regardless of the width set above.
  &__table.v-data-table.theme--light .v-data-table-header tr th,
  &__table.v-data-table.theme--light .v-data-table-header tr th span {
    white-space: nowrap;
  }

  .v-data-table.theme--light tbody td {
    color: $color-normal;
  }

  // Readable column headings, for this table only.
  //
  // TableNormal.vue has an UNSCOPED block setting every v-data-table header to $color-neutral
  // (#ccd4de), which is 1.50:1 against white — the labels are barely there on every admin table in
  // the app. That is a pre-existing app-wide decision and not this page's to overturn, but this is
  // an operations dashboard: six of its seven columns are named things you have to read to know
  // what you are looking at. slate-700 is 6.0:1 and matches the weight of the "?" beside it.
  .v-data-table.theme--light .v-data-table-header tr th {
    color: $color-slate-700;
  }

  .v-data-table__progress {
    .base-loading-bar {
      position: absolute;
      top: 48px;
    }
  }
}
</style>
